import { inject, Pipe, PipeTransform } from '@angular/core';
import { PiiAnonymizationService } from '@maximizer/ai/data-access';
import { AiMarkupUtils } from '@maximizer/ai/ui';
import { escapeHtml } from '@maximizer/shared/util';
import {
  isAdminAnonymizationEnabled,
  OutlookAiApiService,
} from '@maximizer/outlook/ai/data-access';
import { OutlookStore } from '@maximizer/outlook/shared/data-access';
import { TranslateService } from '@ngx-translate/core';

@Pipe({ name: 'outlookDeAnonymizer' })
export class OutlookDeAnonymizerPipe implements PipeTransform {
  private readonly outlookStore = inject(OutlookStore);
  private readonly anonymizerService = inject(OutlookAiApiService);
  private readonly translate = inject(TranslateService);

  private readonly notSharedToAIText = this.translate.instant(
    'outlook.iqBoost.anonymization.notSharedToAI',
  );
  private readonly anonymizedText = this.translate.instant(
    'outlook.iqBoost.anonymization.anonymized',
  );

  transform(value?: string): string {
    if (!value) return '';

    const anonymized =
      isAdminAnonymizationEnabled(
        this.outlookStore.adminDataProcessingFeatures(),
      ) || this.outlookStore.userAnonymizationEnabled();

    // Server wraps restored PII as <anonymized value="…"/>; legacy <TYPE#> masks are handled below.
    const html = AiMarkupUtils.replaceAnonymizedTags(value, (pii) =>
      anonymized ? this.buildAnonymizedBadge(pii) : escapeHtml(pii),
    );

    return anonymized ? this.anonymizeHtml(html) : this.deanonymizeHtml(html);
  }

  private buildAnonymizedBadge(value: string): string {
    return `<span class="inline-block bg-transparent rounded px-1 m-[1px] border border-black font-normal text-black"
        title="${escapeHtml(this.notSharedToAIText)}"
        data-title="${escapeHtml(this.notSharedToAIText)}"
        data-anonymized-value="${escapeHtml(value)}"
      ><i class="fa-icons icon-lock-keyhole icon-14 text-black pr-1"></i>${escapeHtml(value || this.anonymizedText)}</span>`;
  }

  private anonymizeHtml(html: string): string {
    return PiiAnonymizationService.processPiiMaskTokensInHtml(
      html,
      (type, number) => {
        const mask = `${type}${number}`;
        const original = this.anonymizerService.deAnonymize(mask);
        const displayText = original ?? this.anonymizedText;
        return `<span class="inline-block bg-transparent rounded px-1 m-[1px] border border-black font-normal text-black"
        title="${escapeHtml(this.notSharedToAIText)}"
        data-title="${escapeHtml(this.notSharedToAIText)}"
        data-type="${type.toLowerCase()}"
        data-number="${number.toLowerCase()}"
      ><i class="fa-icons icon-lock-keyhole icon-14 text-black pr-1"></i>${escapeHtml(displayText)}</span>`;
      },
    );
  }

  private deanonymizeHtml(html: string): string {
    return PiiAnonymizationService.processPiiMaskTokensInHtml(
      html,
      (type, number) => {
        const mask = `${type}${number}`;
        return this.anonymizerService.deAnonymize(mask) ?? mask;
      },
    );
  }
}
